import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import type { AutoPostPendingResponse } from '@/types/auto-post';

/**
 * Everything auto-post would publish for this tenant, and the rule blocking
 * whatever it will not.
 *
 * The decision is computed server-side by the same function the sweep uses, so
 * this page cannot drift from what actually happens. Nothing here recomputes
 * eligibility in the browser, deliberately — a second implementation of the
 * rule is a second thing to be wrong.
 */
export function useAutoPostPending(locationId: string | null = null) {
  return useQuery({
    queryKey: reviewKeys.autoPostPending(locationId),
    queryFn: () =>
      apiGet<AutoPostPendingResponse>(
        operationsClient,
        reviewPlatformsV2Endpoints.replies.autoPostPending(locationId),
      ),
  });
}

/** Call off one pending auto-post. The draft survives; the review stays open. */
export function useCancelAutoPost(locationId: string | null = null) {
  const queryClient = useQueryClient();

  return useApiMutation<unknown, { externalReviewId: string }>({
    mutationFn: ({ externalReviewId }) =>
      apiPost(
        operationsClient,
        reviewPlatformsV2Endpoints.replies.autoPostCancel(externalReviewId),
        {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.autoPostPending(locationId),
      });
      toast.success('Auto-post cancelled. The draft is still there to edit.');
    },
  });
}
